/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { Box, Tab, Tabs, AppBar } from '@mui/material';
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { apiUrl } from '../utilities/apiUrl';
import {
  General,
  EmployeeAccounts,
  DepartmentReports,
  EmployeeReports,
  Reminders,
  QueryTable,
  PageContainer,
  CustomRangeModal,
} from '../components/index';
import { subMonths, subYears, subDays } from 'date-fns';

export const Manage = () => {
  const [empId] = useOutletContext();
  const [activeEmployeeList, setActiveEmployeeList] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [timesheetAll, setTimesheetAll] = useState([]);
  const [activeTimesheets, setActiveTimesheets] = useState([]);
  const [timesheetCutoff, setTimesheetCutoff] = useState([]);
  const [customTimesheet, setCustomTimesheet] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [value, setValue] = useState(
    parseInt(localStorage.getItem('managetabs')) ?? 0
  );
  const [pickerModalOpen, setPickerModalOpen] = useState(false);
  const [startValue, setStartValue] = useState(null);
  const [endValue, setEndValue] = useState(null);
  const [preDefinedValue, setPreDefinedValue] = useState('');
  const [allEmpView, setAllEmpView] = useState([]);

  useEffect(() => {
    if (employeeList.length === 0) return;
    if (employeeList.find(({ _id }) => _id === empId).role === 'REGULAR_USER') {
      window.location.replace('/forbidden');
    }
  }, [employeeList, empId]);

  useEffect(() => {
    if (parseInt(localStorage.getItem('managetabs')) === 4) {
      setPickerModalOpen(true);
    }
    setValue(parseInt(localStorage.getItem('managetabs')));
  }, [value]);

  // GET CURRENT ACTIVE/CHECKED IN EMPLOYEES
  useEffect(() => {
    fetch(apiUrl('/employee?active=true'), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setActiveEmployeeList(data.data);
        } else {
          window.location.replace('/forbidden');
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  // GET ALL EMPLOYEES
  useEffect(() => {
    fetch(apiUrl('/employee'), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setEmployeeList(data.data);
        } else {
          window.location.replace('/forbidden');
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  // GET TIMESHEET (CUTOFF RANGE ONLY)
  useEffect(() => {
    fetch(apiUrl('/timesheet?cutoff=true'), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setTimesheetCutoff(data.data);
        } else {
          console.log(data.message);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  // GET TIMESHEET OF CURRENT ACTIVE EMPLOYEES
  useEffect(() => {
    fetch(
      apiUrl(
        `/timesheet?startdate=${subDays(new Date(), 1)}&enddate=${new Date()}`
      ),
      {
        method: 'GET',
        credentials: 'include',
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setTimesheetAll(data.data);
          setAllEmpView(
            employeeList.map((emp) => ({
              ...emp,
              ...data.data.find((time) => emp._id === time.empid),
              ...departmentList.find((dept) => emp.deptid === dept._id),
            }))
          );
        } else {
          console.log(data.message);
        }
      })
      .catch((err) => {
        console.log(err);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // GET ALL OPEN TIMESHEETS (checked-in employees, regardless of check-in date)
  // Used by the Check In/Out flow so employees who checked in more than 24h ago
  // are still recognized as active and can be checked out.
  useEffect(() => {
    fetch(apiUrl('/timesheet?active=true'), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setActiveTimesheets(data.data);
        } else {
          console.log(data.message);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  // GET ALL DEPARTMENT DATA
  useEffect(() => {
    fetch(apiUrl('/department'), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        setDepartmentList(data.data);
      })
      .catch((err) => {
        console.log(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TAB CHANGE HANDLER
  const handleChange = (event, newValue) => {
    setValue(newValue);
    localStorage.setItem('managetabs', newValue);
  };

  const handleCustomDate = () => {
    // Set hours of custom date range for start and end date when displaying timesheet
    startValue.setHours(0, 0, 0, 0);
    endValue.setHours(23, 59, 59, 99);

    // CONVERT TIME QUERY IN ISOSTRING TO OFFSET TIMEZONE
    const startValueWithTimeZoneOffset = startValue.toISOString();
    const endValueWithTimeZoneOffset = endValue.toISOString();

    fetch(
      apiUrl(
        `/timesheet?startdate=${startValueWithTimeZoneOffset}&enddate=${endValueWithTimeZoneOffset}`
      ),
      {
        method: 'GET',
        credentials: 'include',
      }
    )
      .then((response) => response.json())
      .then((data) => {
        const noNullCheckOuts = data.data.filter((data) => {
          return data.checkout !== null;
        });
        setCustomTimesheet(
          noNullCheckOuts.map((time) => ({
            ...time,
            ...employeeList.find((emp) => time.empid === emp._id),
            _id: time._id,
          }))
        );
        setPickerModalOpen(false);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleDateRangeChange = (e) => {
    let val = e.target.value;
    setPreDefinedValue(val);
    const today = new Date();
    const threeMonths = subMonths(today, 3);
    const sixMonths = subMonths(today, 6);
    const oneYear = subYears(today, 1);
    const twoYears = subYears(today, 2);

    setEndValue(today);

    val === '3months'
      ? setStartValue(threeMonths)
      : val === '6months'
        ? setStartValue(sixMonths)
        : val === '1year'
          ? setStartValue(oneYear)
          : setStartValue(twoYears);
  };

  return (
    <PageContainer selectedPage={4}>
      <AppBar position="static">
        <Tabs
          value={value}
          onChange={(e, newValue) => {
            handleChange(e, newValue);
            newValue === 4
              ? setPickerModalOpen(true)
              : setPickerModalOpen(false);
          }}
          indicatorColor="secondary"
          textColor="inherit"
          variant="fullWidth"
        >
          {/* <Tab label="General" /> */}
          <Tab label="Employee Accounts" />
          <Tab label="Employee Reports" />
          {/* <Tab label="Department Reports" /> */}
          <Tab label="Timesheet" />
          <Tab label="Reminders" />
        </Tabs>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {
          /*value === 0 ? (
          <General employeeList={employeeList} />
        ) :*/ value === 0 ? (
            <EmployeeAccounts data={employeeList} />
          ) : value === 1 ? (
            <EmployeeReports
              departmentList={departmentList}
              employeeList={employeeList}
              activeEmpData={activeEmployeeList}
              cutoffData={timesheetCutoff}
              timesheetActive={timesheetAll}
              allEmpData={employeeList}
              allEmpView={allEmpView}
            /> /*: value === 2 ? (
          <DepartmentReports departmentList={departmentList} />
        )*/
          ) : value === 2 ? (
            <QueryTable
              timesheetData={customTimesheet}
              timesheetActive={activeTimesheets}
              allEmpData={employeeList}
              cutoff={timesheetCutoff}
              primaryButton={() => setPickerModalOpen(true)}
            />
          ) : (
            <Reminders employeeList={employeeList} />
          )
        }
      </Box>
      <CustomRangeModal
        open={pickerModalOpen}
        onClose={() => setPickerModalOpen(false)}
        startValue={startValue}
        startOnChange={(newValue) => setStartValue(newValue)}
        endValue={endValue}
        endOnChange={(newValue) => setEndValue(newValue)}
        primaryClick={handleCustomDate}
        secondaryClick={() => setPickerModalOpen(false)}
        dateRange={preDefinedValue}
        handleDateRangeChange={handleDateRangeChange}
        showOptions
      />
    </PageContainer>
  );
};
